import { useEffect, useState, useCallback } from "react";
import { Lock, LogOut, Users, CalendarDays, FileText, Share2, Loader2, Activity, ShieldCheck, X } from "lucide-react";
import { api } from "../lib/api";

const TOKEN_KEY = "muse_admin_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) +
      " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 10); }
}

const STATUS_STYLE = {
  "Shared with clinic": "bg-[#8FE3BE]/20 text-[#8FE3BE]",
  "Report ready": "bg-[#B7E4C7]/20 text-[#B7E4C7]",
  "In progress": "bg-white/10 text-white/60",
};

const PRIORITY_STYLE = {
  urgent: "bg-[#D9381E]/25 text-[#FCA5A5]",
  attention: "bg-[#E67300]/25 text-[#FCD9A5]",
  consider: "bg-[#2E8540]/25 text-[#A7E8BE]",
};
const PRIORITY_LABEL = { urgent: "Act soon", attention: "Needs attention", consider: "Consider" };

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-white/[0.06] border border-white/10 p-4">
      <div className="w-9 h-9 rounded-xl bg-[#8FE3BE]/15 flex items-center justify-center mb-3">
        <Icon className="w-4.5 h-4.5 text-[#8FE3BE]" />
      </div>
      <p className="font-head font-extrabold text-3xl leading-none text-white">{value}</p>
      <p className="text-[12px] text-white/55 mt-1.5">{label}</p>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadStats = useCallback(async (tkn) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stats", { headers: { Authorization: `Bearer ${tkn}` } });
      setStats(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Session expired. Please sign in again.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadStats(token);
  }, [token, loadStats]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setPassword("");
      setToken(r.data.token);
    } catch (err) {
      setError("Incorrect password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center px-6" style={{ background: "#0f3324" }}>
        <form onSubmit={login} data-testid="admin-login-form"
          className="w-full max-w-sm rounded-3xl bg-white/[0.06] border border-white/10 p-7 backdrop-blur">
          <div className="w-14 h-14 rounded-2xl bg-[#8FE3BE]/15 flex items-center justify-center mb-5">
            <Lock className="w-6 h-6 text-[#8FE3BE]" />
          </div>
          <h1 className="font-head font-extrabold text-2xl text-white">Owner dashboard</h1>
          <p className="text-[13px] text-white/55 mt-1.5 mb-6">Muse Charitable Dental Clinic — private access.</p>
          <label className="text-[12px] font-semibold text-white/70">Password</label>
          <input data-testid="admin-password-input" type="password" value={password} autoFocus
            onChange={(e) => setPassword(e.target.value)} placeholder="Enter password"
            className="mt-1.5 w-full h-12 rounded-xl bg-white/10 border border-white/15 px-4 text-white placeholder:text-white/30 outline-none focus:border-[#8FE3BE]/60" />
          {error && <p data-testid="admin-login-error" className="text-[12.5px] text-red-300 mt-3">{error}</p>}
          <button data-testid="admin-login-submit" type="submit" disabled={busy || !password}
            className="mt-5 w-full h-12 rounded-xl bg-[#8FE3BE] text-[#0f3324] font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sign in
          </button>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen w-full" style={{ background: "#0f3324" }}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
        <header className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-head font-extrabold text-2xl sm:text-3xl text-white">Owner dashboard</h1>
            <p className="text-[13px] text-white/55 mt-0.5">{stats?.clinic?.name || "Muse Charitable Dental Clinic"}</p>
          </div>
          <button data-testid="admin-logout" onClick={logout}
            className="tap flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold px-4 py-2 transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </header>

        {loading && !stats ? (
          <div className="flex items-center justify-center py-24 text-white/60"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : stats ? (
          <div className="flex flex-col gap-6" data-testid="admin-dashboard">
            {/* Metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Metric testid="metric-total" icon={Users} label="Total patients scanned" value={stats.total_patients} />
              <Metric testid="metric-today" icon={CalendarDays} label="Scanned today" value={stats.today_patients} />
              <Metric testid="metric-reports" icon={FileText} label="Reports generated" value={stats.reports_generated} />
              <Metric testid="metric-shared" icon={Share2} label="Shared with clinic (WhatsApp / Email)" value={stats.shared_total} />
            </div>

            {/* Findings summary */}
            <section className="rounded-2xl bg-white/[0.06] border border-white/10 p-5" data-testid="admin-findings">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-[#8FE3BE]" />
                <h2 className="font-head font-bold text-white">Findings summary</h2>
                <span className="text-[11px] text-white/40">across all scans</span>
              </div>
              {stats.findings.length === 0 ? (
                <p className="text-[13px] text-white/50">No findings recorded yet.</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {stats.findings.map((f, i) => (
                    <li key={i} data-testid={`finding-${i}`}
                      className="flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 pl-3 pr-2 py-1.5">
                      <span className="text-[13px] text-white/85">{f.label}</span>
                      <span className="text-[12px] font-bold text-[#0f3324] bg-[#8FE3BE] rounded-full px-2 py-0.5">{f.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Patient list */}
            <section className="rounded-2xl bg-white/[0.06] border border-white/10 overflow-hidden" data-testid="admin-patients">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
                <Users className="w-4 h-4 text-[#8FE3BE]" />
                <h2 className="font-head font-bold text-white">Patients</h2>
                <span className="text-[11px] text-white/40">{stats.patients.length} total · tap a row for full details</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-white/40">
                      <th className="px-5 py-3 font-semibold">Name</th>
                      <th className="px-5 py-3 font-semibold">Phone</th>
                      <th className="px-5 py-3 font-semibold">Age / Sex</th>
                      <th className="px-5 py-3 font-semibold">Complaint</th>
                      <th className="px-5 py-3 font-semibold">Scan date</th>
                      <th className="px-5 py-3 font-semibold">Priority</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.patients.length === 0 ? (
                      <tr><td colSpan={7} className="px-5 py-8 text-center text-white/50 text-[13px]">No patients yet.</td></tr>
                    ) : stats.patients.map((p, i) => (
                      <tr key={i} data-testid={`patient-row-${i}`} onClick={() => setSelected(p)}
                        className="border-t border-white/5 cursor-pointer hover:bg-white/[0.04] transition-colors">
                        <td className="px-5 py-3 text-[13.5px] text-white/90 whitespace-nowrap">{p.name}</td>
                        <td className="px-5 py-3 text-[13.5px] text-white/70 whitespace-nowrap">{p.phone}</td>
                        <td className="px-5 py-3 text-[13px] text-white/70 whitespace-nowrap">{p.age ?? "—"} · {p.sex}</td>
                        <td className="px-5 py-3 text-[13px] text-white/70 max-w-[180px] truncate">{p.chief_complaint}</td>
                        <td className="px-5 py-3 text-[12.5px] text-white/60 whitespace-nowrap">{fmtDate(p.scan_date)}</td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          {p.overall ? <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${PRIORITY_STYLE[p.overall] || "bg-white/10 text-white/60"}`}>{PRIORITY_LABEL[p.overall] || p.overall}</span> : <span className="text-white/30 text-[12px]">—</span>}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[p.status] || "bg-white/10 text-white/60"}`}>{p.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        ) : null}
      </div>

      {/* Patient detail modal */}
      {selected && (
        <div data-testid="admin-detail-modal" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-white/10 p-6"
            style={{ background: "#123a2b" }}>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="font-head font-extrabold text-xl text-white">{selected.name !== "—" ? selected.name : "Unnamed patient"}</h3>
                <p className="text-[12.5px] text-white/50 mt-0.5">{fmtDate(selected.scan_date)}</p>
              </div>
              <button data-testid="admin-detail-close" onClick={() => setSelected(null)}
                className="tap w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-5">
              {[["Phone", selected.phone], ["Email", selected.email], ["Age", selected.age ?? "—"], ["Sex", selected.sex],
                ["Photos", `${selected.photos_count}`], ["Status", selected.status]].map(([k, v]) => (
                <div key={k} className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2">
                  <p className="text-[10.5px] uppercase tracking-wide text-white/40">{k}</p>
                  <p className="text-[13.5px] text-white/90 mt-0.5 break-words">{v}</p>
                </div>
              ))}
            </div>

            <div className="mb-4">
              <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Chief complaint</p>
              <p className="text-[14px] text-white/90">{selected.chief_complaint}</p>
            </div>

            {selected.overall && (
              <div className="mb-4">
                <span className={`text-[12px] font-semibold px-3 py-1 rounded-full ${PRIORITY_STYLE[selected.overall] || "bg-white/10 text-white/60"}`}>
                  Overall: {PRIORITY_LABEL[selected.overall] || selected.overall}
                </span>
              </div>
            )}

            {selected.report_findings?.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Findings</p>
                <ul className="flex flex-col gap-2">
                  {selected.report_findings.map((f, i) => (
                    <li key={i} className="rounded-xl bg-white/[0.05] border border-white/10 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[13.5px] font-semibold text-white/90">{f.finding}</span>
                        <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${PRIORITY_STYLE[f.priority] || "bg-white/10 text-white/60"}`}>{PRIORITY_LABEL[f.priority] || f.priority}</span>
                      </div>
                      {f.location && <p className="text-[12px] text-white/55 mt-1">Location: {f.location}</p>}
                      {f.evidence && <p className="text-[12px] text-white/55 mt-0.5">{f.evidence}</p>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selected.xray_findings?.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">IOPA / OPG findings</p>
                {selected.xray_findings.map((xf, i) => (
                  <div key={i} className="mb-2">
                    <p className="text-[12.5px] text-white/70 mb-1">{xf.view}</p>
                    {xf.labels.length > 0 ? (
                      <ul className="flex flex-wrap gap-2">
                        {xf.labels.map((l, j) => <li key={j} className="text-[12px] font-semibold px-2.5 py-1 rounded-full bg-[#FCEBE8]/90 text-[#9E2A16]">{l}</li>)}
                      </ul>
                    ) : <p className="text-[12px] text-white/40">No significant findings</p>}
                  </div>
                ))}
              </div>
            )}

            {selected.photos?.length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-wide text-white/40 mb-2">Photos ({selected.photos.length})</p>
                <ul className="flex flex-wrap gap-2">
                  {selected.photos.map((ph, i) => (
                    <li key={i} className="text-[12px] px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-white/75">
                      {ph.view}{ph.quality_score != null ? ` · ${ph.quality_score}/100` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selected.limitations?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">Limitations</p>
                <ul className="list-disc pl-5 text-[12.5px] text-white/60">
                  {selected.limitations.map((l, i) => <li key={i}>{l}</li>)}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
